/**
 * TwoFactorSettings Component - Enhanced with offline 2FA methods
 * Supports SMS, TOTP (Authenticator Apps), Backup Codes, and Trusted Devices
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First & Desktop-Ready)
 */

import React, { useState, useEffect } from 'react';
import { Shield, Phone, Smartphone, Key, Check, AlertCircle, Download, Copy, RefreshCw, Trash2, Monitor, X } from 'lucide-react';
import { config } from '@/src/config';
import { cn } from '@/lib/utils';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Product-aware path resolution for 2FA endpoints. Triveny tenant users live
// in platform_shared.tenant_users and authenticate against the platform auth
// surface; the legacy /api/auth/2fa/* endpoints sit behind healthcare
// get_current_user and reject platform JWTs with 401 "Invalid token".
//
// The platform 2FA router has DIFFERENT path shapes than the healthcare one
// (e.g. /setup-totp vs /totp/setup), so this isn't a simple prefix swap —
// keep the mapping explicit so a backend rename in either tree is obvious
// in this file.
type TwoFactorAction =
  | 'methods'
  | 'trustedDevices'
  | 'trustedDeviceById'
  | 'trustedDevicesRevokeAll'
  | 'totpSetup'
  | 'totpVerifySetup'
  | 'totpDisable'
  | 'backupCodesGenerate'
  | 'preferredMethod';

function twoFactorPath(action: TwoFactorAction, deviceId?: number | string): string {
  if (config.isTriveny) {
    const base = '/api/platform/auth/2fa';
    switch (action) {
      case 'methods': return `${base}/methods`;
      case 'trustedDevices': return `${base}/trusted-devices`;
      case 'trustedDeviceById': return `${base}/trusted-devices/${deviceId}`;
      case 'totpSetup': return `${base}/setup-totp`;
      case 'totpVerifySetup': return `${base}/verify-totp-setup`;
      case 'totpDisable': return `${base}/disable`;
      case 'backupCodesGenerate': return `${base}/backup-codes/regenerate`;
      // Platform router has no preferred-method or revoke-all endpoints yet.
      // The caller hides those UI controls when isTriveny — but if they're
      // ever wired up, return a path that 404s with a clear name so the
      // missing backend route is obvious in network logs.
      case 'preferredMethod': return `${base}/preferred-method`; // NOT IMPLEMENTED on platform
      case 'trustedDevicesRevokeAll': return `${base}/trusted-devices`; // NOT IMPLEMENTED on platform
    }
  }
  const base = '/api/auth/2fa';
  switch (action) {
    case 'methods': return `${base}/methods`;
    case 'trustedDevices': return `${base}/trusted-devices`;
    case 'trustedDeviceById': return `${base}/trusted-devices/${deviceId}`;
    case 'trustedDevicesRevokeAll': return `${base}/trusted-devices`;
    case 'totpSetup': return `${base}/totp/setup`;
    case 'totpVerifySetup': return `${base}/totp/verify-setup`;
    case 'totpDisable': return `${base}/totp/disable`;
    case 'backupCodesGenerate': return `${base}/backup-codes/generate`;
    case 'preferredMethod': return `${base}/preferred-method`;
  }
}

interface UserMethods {
  sms_enabled: boolean;
  totp_enabled: boolean;
  backup_codes_count: number;
  trusted_devices_count: number;
  preferred_method: string;
}

interface TOTPSetup {
  secret: string;
  qr_code: string;
  message: string;
}

interface TrustedDevice {
  id: number;
  device_name: string;
  ip_address: string | null;
  last_used: string;
  created_at: string;
  expires_at: string;
}

export const TwoFactorSettings: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [methods, setMethods] = useState<UserMethods | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // TOTP Setup
  const [showTOTPSetup, setShowTOTPSetup] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TOTPSetup | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  // Backup Codes
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupLoading, setBackupLoading] = useState(false);

  // Trusted Devices
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);

  // Preferred Method
  const [preferenceLoading, setPreferenceLoading] = useState(false);

  useEffect(() => {
    fetchMethods();
    fetchTrustedDevices();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  };

  const fetchMethods = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('methods')}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load 2FA methods');
      }

      const data = await response.json();
      setMethods(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrustedDevices = async () => {
    try {
      setDevicesLoading(true);
      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('trustedDevices')}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load trusted devices');
      }

      const data = await response.json();
      setTrustedDevices(data.devices || []);
    } catch (err: any) {
      console.error('Failed to load trusted devices:', err);
    } finally {
      setDevicesLoading(false);
    }
  };

  // TOTP Handlers
  const handleSetupTOTP = async () => {
    try {
      setTotpLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('totpSetup')}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to setup authenticator app');
      }

      const data = await response.json();
      setTotpSetup(data);
      setShowTOTPSetup(true);
    } catch (err: any) {
      setError(err.message || 'Failed to setup authenticator app');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerifyTOTP = async () => {
    if (totpCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setTotpLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('totpVerifySetup')}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code: totpCode })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Invalid code');
      }

      setSuccess('Authenticator app enabled successfully!');
      setShowTOTPSetup(false);
      setTotpSetup(null);
      setTotpCode('');
      await fetchMethods();
    } catch (err: any) {
      setError(err.message || 'Failed to verify code');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisableTOTP = async () => {
    if (!confirm('Are you sure you want to disable your authenticator app?')) {
      return;
    }

    try {
      setTotpLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('totpDisable')}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to disable authenticator app');
      }

      setSuccess('Authenticator app disabled');
      await fetchMethods();
    } catch (err: any) {
      setError(err.message || 'Failed to disable authenticator app');
    } finally {
      setTotpLoading(false);
    }
  };

  // Backup Codes Handlers
  const handleGenerateBackupCodes = async () => {
    if (methods && methods.backup_codes_count > 0) {
      if (!confirm('This will invalidate all existing backup codes. Continue?')) {
        return;
      }
    }

    try {
      setBackupLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('backupCodesGenerate')}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to generate backup codes');
      }

      const data = await response.json();
      setBackupCodes(data.codes);
      setShowBackupCodes(true);
      setSuccess('Backup codes generated successfully');
      await fetchMethods();
    } catch (err: any) {
      setError(err.message || 'Failed to generate backup codes');
    } finally {
      setBackupLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setSuccess('Backup codes copied to clipboard');
  };

  const downloadBackupCodes = () => {
    const codesText = `Trust Marketplace Backup Codes\nGenerated: ${new Date().toISOString()}\n\n${backupCodes.join('\n')}\n\nEach code can only be used once.\nStore these codes in a safe place.`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trust-marketplace-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Preferred Method Handlers
  const handleUpdatePreferredMethod = async (method: string) => {
    try {
      setPreferenceLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('preferredMethod')}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ preferred_method: method })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update preferred method');
      }

      setSuccess(`Preferred method updated to ${method === 'totp' ? 'Authenticator App' : 'SMS'}`);
      await fetchMethods();
    } catch (err: any) {
      setError(err.message || 'Failed to update preferred method');
    } finally {
      setPreferenceLoading(false);
    }
  };

  // Trusted Devices Handlers
  const handleRevokeDevice = async (deviceId: number) => {
    if (!confirm('Are you sure you want to revoke this device? You will need to verify with 2FA next time you log in from this device.')) {
      return;
    }

    try {
      setDevicesLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('trustedDeviceById', deviceId)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to revoke device');
      }

      setSuccess('Device revoked successfully');
      await fetchTrustedDevices();
      await fetchMethods();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke device');
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleRevokeAllDevices = async () => {
    if (!confirm('Are you sure you want to revoke ALL trusted devices? You will need to verify with 2FA on all devices next time you log in.')) {
      return;
    }

    try {
      setDevicesLoading(true);
      setError(null);

      const response = await fetch(`${config.weaveinApi.baseUrl}${twoFactorPath('trustedDevicesRevokeAll')}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to revoke devices');
      }

      setSuccess('All trusted devices revoked');
      await fetchTrustedDevices();
      await fetchMethods();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke devices');
    } finally {
      setDevicesLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return fmt.relative(dateStr);
  };

  if (loading && !methods) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('settings.loading_2fa_settings', 'Loading 2FA settings...')}</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-8 h-8 text-blue-600 flex-shrink-0" />
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{t('settings.twofactor_authentication', 'Two-Factor Authentication')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('settings.protect_your_account_with_multiple', 'Protect your account with multiple security methods')}</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Success Alert */}
      {success && (
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-600">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{success}</span>
          </div>
          <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* SMS Authentication */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.sms_authentication', 'SMS Authentication')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.receive_verification_codes_via_text', 'Receive verification codes via text message')}</p>
            </div>
          </div>
          <span className={cn(
            "px-3 py-1 rounded-full text-xs font-medium",
            methods?.sms_enabled
              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
          )}>
            {methods?.sms_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {methods?.sms_enabled
            ? 'SMS authentication is active. You will receive verification codes via text message when logging in.'
            : 'Add a phone number to your profile to enable SMS authentication.'}
        </p>
      </div>

      {/* Authenticator App */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.authenticator_app', 'Authenticator App')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Use Google Authenticator, Authy, or similar apps (works offline)</p>
            </div>
          </div>
          <span className={cn(
            "px-3 py-1 rounded-full text-xs font-medium",
            methods?.totp_enabled
              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
          )}>
            {methods?.totp_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        {!methods?.totp_enabled ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Authenticator apps generate time-based codes that work even without internet connection.
              Perfect for situations where you only have WiFi access (like on an airplane).
            </p>
            <button
              onClick={handleSetupTOTP}
              disabled={totpLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Smartphone className="w-4 h-4" />
              {totpLoading ? 'Setting up...' : 'Set Up Authenticator App'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('settings.your_authenticator_app_is_active', 'Your authenticator app is active and generating verification codes.')}
            </p>
            <button
              onClick={handleDisableTOTP}
              disabled={totpLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {t('settings.disable_authenticator_app', 'Disable Authenticator App')}
            </button>
          </div>
        )}
      </div>

      {/* Backup Codes */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.backup_codes', 'Backup Codes')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Emergency access codes (use when you can't access your phone or authenticator)</p>
            </div>
          </div>
          {methods && methods.backup_codes_count > 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
              {methods.backup_codes_count} codes available
            </span>
          )}
        </div>
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Backup codes are single-use codes that can be used to log in if you lose access to your phone
            or authenticator app. Generate and save them in a secure location.
          </p>
          <button
            onClick={handleGenerateBackupCodes}
            disabled={backupLoading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            {methods && methods.backup_codes_count > 0
              ? 'Regenerate Backup Codes'
              : 'Generate Backup Codes'}
          </button>
        </div>
      </div>

      {/* Trusted Devices */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Monitor className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.trusted_devices', 'Trusted Devices')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.devices_that_can_skip_2fa', 'Devices that can skip 2FA for 30 days')}</p>
            </div>
          </div>
          {methods && methods.trusted_devices_count > 0 && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
              {methods.trusted_devices_count} device{methods.trusted_devices_count > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {t('settings.when_you_check_trust_this', 'When you check "Trust this device for 30 days" during login, that device won\'t require 2FA codes for 30 days.')}
        </p>

        {devicesLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.loading_devices', 'Loading devices...')}</p>
        ) : trustedDevices.length > 0 ? (
          <div className="space-y-3">
            <div className="space-y-2">
              {trustedDevices.map((device) => (
                <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white text-sm">{device.device_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Last used: {formatDate(device.last_used)}
                      {device.ip_address && <span> • IP: {device.ip_address}</span>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeDevice(device.id)}
                    disabled={devicesLoading}
                    title={t('settings.revoke_this_device', 'Revoke this device')}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={handleRevokeAllDevices}
              disabled={devicesLoading}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {t('settings.revoke_all_devices', 'Revoke All Devices')}
            </button>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('settings.no_trusted_devices_check_trust', 'No trusted devices. Check "Trust this device for 30 days" during your next login to add one.')}
          </p>
        )}
      </div>

      {/* Preferred Method */}
      {methods && (methods.sms_enabled || methods.totp_enabled) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.preferred_method', 'Preferred Method')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.default_2fa_method_when_logging', 'Default 2FA method when logging in')}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {t('settings.choose_your_preferred_authentication_method', 'Choose your preferred authentication method. You can still use other enabled methods during login.')}
          </p>

          <div className="space-y-2">
            {methods.sms_enabled && (
              <label className={cn(
                "flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-colors",
                methods.preferred_method === 'sms'
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              )}>
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="preferred_method"
                    value="sms"
                    checked={methods.preferred_method === 'sms'}
                    onChange={() => handleUpdatePreferredMethod('sms')}
                    disabled={preferenceLoading}
                    className="hidden"
                  />
                  <Phone className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">SMS</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{t('settings.text_message_verification_codes', 'Text message verification codes')}</div>
                  </div>
                </div>
                {methods.preferred_method === 'sms' && (
                  <Check className="w-5 h-5 text-blue-600" />
                )}
              </label>
            )}

            {methods.totp_enabled && (
              <label className={cn(
                "flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-colors",
                methods.preferred_method === 'totp'
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              )}>
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="preferred_method"
                    value="totp"
                    checked={methods.preferred_method === 'totp'}
                    onChange={() => handleUpdatePreferredMethod('totp')}
                    disabled={preferenceLoading}
                    className="hidden"
                  />
                  <Smartphone className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{t('settings.authenticator_app_1', 'Authenticator App')}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Time-based codes (works offline)</div>
                  </div>
                </div>
                {methods.preferred_method === 'totp' && (
                  <Check className="w-5 h-5 text-blue-600" />
                )}
              </label>
            )}
          </div>
        </div>
      )}

      {/* TOTP Setup Modal */}
      {showTOTPSetup && totpSetup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.set_up_authenticator_app', 'Set Up Authenticator App')}</h3>
            </div>

            <div className="p-6 space-y-6">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full font-semibold text-sm flex-shrink-0">1</div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{t('settings.install_an_authenticator_app', 'Install an authenticator app')}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {t('settings.download_google_authenticator_microsoft_authentica', 'Download Google Authenticator, Microsoft Authenticator, Authy, or any TOTP-compatible app.')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full font-semibold text-sm flex-shrink-0">2</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{t('settings.scan_this_qr_code', 'Scan this QR code')}</p>
                  <div className="mt-3 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 dark:border-gray-700 rounded-lg inline-block">
                    <img src={totpSetup.qr_code} alt="QR Code" className="w-48 h-48" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-3">
                    {t('settings.or_enter_this_code_manually', 'Or enter this code manually:')} <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">{totpSetup.secret}</code>
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full font-semibold text-sm flex-shrink-0">3</div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">{t('settings.enter_the_6digit_code_from', 'Enter the 6-digit code from your app')}</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    autoFocus
                    className="mt-3 w-full max-w-[200px] px-4 py-3 text-center text-2xl font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowTOTPSetup(false);
                  setTotpSetup(null);
                  setTotpCode('');
                }}
                disabled={totpLoading}
                className="px-5 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('settings.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleVerifyTOTP}
                disabled={totpCode.length !== 6 || totpLoading}
                className="px-5 py-2.5 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {totpLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Codes Modal */}
      {showBackupCodes && backupCodes.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.save_your_backup_codes', 'Save Your Backup Codes')}</h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Warning */}
              <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-800">{t('settings.important_save_these_codes_now', 'Important: Save these codes now')}</p>
                  <p className="text-sm text-yellow-700 mt-1">{t('settings.each_code_can_only_be', 'Each code can only be used once. Store them in a safe place.')}</p>
                </div>
              </div>

              {/* Codes Grid */}
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg font-mono text-sm">
                    <span className="text-gray-400">{index + 1}.</span>
                    <code className="text-gray-900 dark:text-white">{code}</code>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={copyBackupCodes}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <Copy className="w-4 h-4" />
                {t('settings.copy_all', 'Copy All')}
              </button>
              <button
                onClick={downloadBackupCodes}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                {t('settings.download', 'Download')}
              </button>
              <button
                onClick={() => {
                  setShowBackupCodes(false);
                  setBackupCodes([]);
                }}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Check className="w-4 h-4" />
                {t('settings.ive_saved_my_codes', 'I\'ve Saved My Codes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
